import React from 'react';
import {
  Users,
  DollarSign,
  Activity,
  Wifi,
  Building,
  FileText,
  CheckCircle,
  AlertTriangle,
  TrendingUp
} from 'lucide-react';
import ResumoCard from './ResumoCard';
import { formatarMoeda } from 'utils/formatters';
import { useReceivableTerm } from 'hooks/useReceivableTerm';

// Função para formatar valores grandes de forma abreviada
const formatarValorAbreviado = (valor) => {
  if (typeof valor !== 'number') return valor;
  
  if (valor >= 1000000) {
    return `${(valor / 1000000).toFixed(1)}M`;
  } else if (valor >= 1000) {
    return `${(valor / 1000).toFixed(1)}K`;
  }
  return valor.toString();
};

const formatarValorAbsoluto = (valor) => {
  if (typeof valor !== 'number') return valor;
  return valor.toLocaleString('pt-BR', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2
  });
};

// Função para formatar moeda de forma abreviada
const formatarMoedaAbreviada = (valor) => {
  if (typeof valor !== 'number') return formatarMoeda(valor);
  
  if (valor >= 1000000) {
    return `R$ ${(valor / 1000000).toFixed(1)}M`;
  } else if (valor >= 1000) {
    return `R$ ${(valor / 1000).toFixed(1)}K`;
  }
  return formatarMoeda(valor);
};

const formatarMoedaAbsoluta = (valor) => {
  if (typeof valor !== 'number') return formatarMoeda(valor);
  return `R$ ${valor.toLocaleString('pt-BR', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2
  })}`;
};

// Função para formatar valores duplos mantendo tamanho de fonte consistente
const getDualValueTone = (label = '') => {
  const normalized = label.toLowerCase();
  if (normalized.includes('inativ') || normalized.includes('suspens') || normalized.includes('problema') || normalized === 'pj') {
    return 'warning';
  }
  if (normalized.includes('ativo') || normalized.includes('ok') || normalized === 'pf') {
    return 'positive';
  }
  return 'neutral';
};

const formatarValorDuplo = (valor1, valor2, label1, label2, usarAbsoluto = false) => {
  const rows = [
    { valor: valor1, label: label1, tone: getDualValueTone(label1) },
    { valor: valor2, label: label2, tone: getDualValueTone(label2) }
  ];

  const toneClass = {
    positive: 'bg-emerald-400/80',
    warning: 'bg-amber-400/80',
    neutral: 'bg-sky-400/70'
  };

  return (
    <div className="space-y-1.5">
      {rows.map((row) => (
        <div key={row.label} className="flex items-center gap-2 leading-tight">
          <span className={`h-1.5 w-1.5 rounded-full ${toneClass[row.tone]}`} aria-hidden="true" />
          <span className="min-w-[3.8rem] text-[1.08rem] font-semibold tabular-nums text-[color:var(--text-primary)]">
            {usarAbsoluto ? formatarValorAbsoluto(row.valor) : formatarValorAbreviado(row.valor)}
          </span>
          <span className="truncate text-[0.96rem] font-medium text-[color:var(--text-muted)]">
            {row.label}
          </span>
        </div>
      ))}
    </div>
  );
};

const ResumoCards = ({ resumo, resumoTitulos, loading = false, usarValoresAbsolutos = false }) => {
  const term = useReceivableTerm();

  if (loading || (!resumo && !resumoTitulos)) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3 sm:gap-4 mb-5 items-stretch">
        {Array.from({ length: 6 }).map((_, index) => (
          <div
            key={index}
            className="animate-pulse rounded-3xl border border-[color:var(--border)] bg-[color:var(--surface)] shadow-[0_4px_12px_rgba(0,0,0,0.14)] backdrop-blur p-4"
          >
            <div className="h-4 bg-[color:var(--surface-muted)] rounded mb-2"></div>
            <div className="h-8 bg-[color:var(--surface-muted)] rounded mb-2"></div>
            <div className="h-3 bg-[color:var(--surface-muted)] rounded"></div>
          </div>
        ))}
      </div>
    );
  }

  // Dashboard de Títulos
  if (resumoTitulos) {
    let dadosProcessados = resumoTitulos;
    
    if (Array.isArray(resumoTitulos) && resumoTitulos.length > 0) {
      dadosProcessados = resumoTitulos[0];
    }
    
    if (resumoTitulos?.data && Array.isArray(resumoTitulos.data) && resumoTitulos.data.length > 0) {
      dadosProcessados = resumoTitulos.data[0];
    }

    const cards = [
      {
        titulo: `Total de ${term.plural}`,
        tituloMobile: `Total ${term.plural}`,
        valor: usarValoresAbsolutos
          ? formatarValorAbsoluto(dadosProcessados.total_titulos_emitidos || 0)
          : formatarValorAbreviado(dadosProcessados.total_titulos_emitidos || 0),
        subtitulo: 'Emitidos',
        icone: FileText,
        corIcone: 'blue',
        valorCompleto: dadosProcessados.total_titulos_emitidos || 0
      },
      {
        titulo: term.receivedLabel,
        tituloMobile: 'Recebidas',
        valor: usarValoresAbsolutos
          ? formatarValorAbsoluto(dadosProcessados.total_titulos_recebidos || 0)
          : formatarValorAbreviado(dadosProcessados.total_titulos_recebidos || 0),
        subtitulo: 'Pagos',
        icone: CheckCircle,
        corIcone: 'green',
        valorCompleto: dadosProcessados.total_titulos_recebidos || 0
      },
      {
        titulo: term.pendingLabel,
        tituloMobile: 'Pendentes',
        valor: usarValoresAbsolutos
          ? formatarValorAbsoluto(dadosProcessados.total_titulos_a_receber || 0)
          : formatarValorAbreviado(dadosProcessados.total_titulos_a_receber || 0),
        subtitulo: 'Em aberto',
        icone: AlertTriangle,
        corIcone: 'orange',
        valorCompleto: dadosProcessados.total_titulos_a_receber || 0
      },
      {
        titulo: 'Valor Total',
        tituloMobile: 'Valor Total',
        valor: usarValoresAbsolutos
          ? formatarMoedaAbsoluta(dadosProcessados.valor_total_emitido || 0)
          : formatarMoedaAbreviada(dadosProcessados.valor_total_emitido || 0),
        subtitulo: 'Emitido',
        icone: DollarSign,
        corIcone: 'green',
        valorCompleto: dadosProcessados.valor_total_emitido || 0
      },
      {
        titulo: 'Valor Recebido (valor líquido)',
        tituloMobile: 'Recebido Líq.',
        valor: usarValoresAbsolutos
          ? formatarMoedaAbsoluta(dadosProcessados.valor_total_recebido || 0)
          : formatarMoedaAbreviada(dadosProcessados.valor_total_recebido || 0),
        subtitulo: 'Pago líquido',
        icone: TrendingUp,
        corIcone: 'purple',
        valorCompleto: dadosProcessados.valor_total_recebido || 0
      },
      {
        titulo: 'Valor Pendente',
        tituloMobile: 'Valor Pendente',
        valor: usarValoresAbsolutos
          ? formatarMoedaAbsoluta(dadosProcessados.valor_restante_a_receber || 0)
          : formatarMoedaAbreviada(dadosProcessados.valor_restante_a_receber || 0),
        subtitulo: 'Valor a receber',
        icone: DollarSign,
        corIcone: 'red',
        valorCompleto: dadosProcessados.valor_restante_a_receber || 0
      }
    ];

    return (
      <div className="mb-5">
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3 sm:gap-4 items-stretch">
          {cards.map((card, index) => (
            <ResumoCard
              key={index}
              titulo={card.titulo}
              tituloMobile={card.tituloMobile}
              valor={card.valor}
              subtitulo={card.subtitulo}
              icone={card.icone}
              corIcone={card.corIcone}
              valorCompleto={card.valorCompleto}
              featured={index < 2}
              className="h-full"
            />
          ))}
        </div>
      </div>
    );
  }

  // Dashboard de Contratos
  if (resumo) {
    const cards = [
      {
        titulo: 'Total Contratos',
        valor: usarValoresAbsolutos
          ? formatarValorAbsoluto(resumo.totalContratos || 0)
          : formatarValorAbreviado(resumo.totalContratos || 0),
        subtitulo: 'Contratos ativos',
        icone: Users,
        corIcone: 'blue',
        featured: true,
        valorCompleto: resumo.totalContratos || 0
      },
      {
        titulo: 'Valor Total',
        valor: usarValoresAbsolutos
          ? formatarMoedaAbsoluta(resumo.valorTotal || 0)
          : formatarMoedaAbreviada(resumo.valorTotal || 0),
        subtitulo: 'Valor em contratos',
        icone: DollarSign,
        corIcone: 'green',
        featured: true,
        valorCompleto: resumo.valorTotal || 0
      },
      {
        titulo: 'Status Contratos',
        valor: formatarValorDuplo(
          resumo.ativos || 0, 
          resumo.suspensos || 0, 
          'Ativos', 
          'Suspensos',
          usarValoresAbsolutos
        ),
        subtitulo: 'Distribuição por status',
        icone: Activity,
        corIcone: 'purple',
        featured: false
      },
      {
        titulo: 'Status Internet',
        valor: formatarValorDuplo(
          resumo.internetOk || 0, 
          resumo.internetProblema || 0, 
          'OK', 
          'Problemas',
          usarValoresAbsolutos
        ),
        subtitulo: 'Qualidade da conexão',
        icone: Wifi,
        corIcone: 'orange',
        featured: false
      },
      {
        titulo: 'Tipo de Pessoa',
        valor: formatarValorDuplo(
          resumo.pessoasFisicas || 0, 
          resumo.pessoasJuridicas || 0, 
          'PF', 
          'PJ',
          usarValoresAbsolutos
        ),
        subtitulo: 'Distribuição por tipo',
        icone: Building,
        corIcone: 'pink',
        featured: false
      }
    ];

    return (
      <div className="mb-6">
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3 sm:gap-4">
          {cards.map((card, index) => {
            return (
              <ResumoCard
                key={index}
                titulo={card.titulo}
                valor={card.valor}
                subtitulo={card.subtitulo}
                icone={card.icone}
                corIcone={card.corIcone}
                valorCompleto={card.valorCompleto}
                featured={card.featured}
                className="h-full"
              />
            );
          })}
        </div>
      </div>
    );
  }

  // Fallback
  return (
    <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-2 sm:gap-4 mb-6 items-stretch">
      {[1, 2, 3, 4, 5, 6].map((index) => (
        <div key={index} className="rounded-lg border border-[color:var(--border)] bg-[color:var(--surface)] shadow-[0_4px_12px_rgba(0,0,0,0.14)] backdrop-blur p-4 animate-pulse">
          <div className="h-4 bg-[color:var(--surface-muted)] rounded mb-2"></div>
          <div className="h-8 bg-[color:var(--surface-muted)] rounded mb-2"></div>
          <div className="h-3 bg-[color:var(--surface-muted)] rounded"></div>
        </div>
      ))}
    </div>
  );
};

export default ResumoCards;
